import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { OLD_DOCTOR_SLUGS, doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { notifyForm } from "@/lib/notifyForm";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Star,
  BadgeCheck,
  GraduationCap,
  Briefcase,
  Stethoscope,
  Clock,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Award,
  Users,
} from "lucide-react";

const AWARD_RX = /\b(award|awarded|gold medal|silver medal|prize|honou?r|honou?red|recipient|recognition|best\s+(doctor|surgeon|physician)|rank holder|merit)\b/i;
const MEMBERSHIP_RX = /\b(member|membership|fellow of|life member|associate member|society|association|college of|academy of|council of)\b/i;

function partitionEducation(items: string[]) {
  const education: string[] = [];
  const awards: string[] = [];
  const memberships: string[] = [];
  for (const raw of items) {
    const item = (raw ?? "").trim();
    if (!item) continue;
    if (AWARD_RX.test(item)) awards.push(item);
    else if (MEMBERSHIP_RX.test(item)) memberships.push(item);
    else education.push(item);
  }
  return { education, awards, memberships };
}
import { getDoctorBySlug, listPublishedDoctors } from "@/lib/doctors.functions";
import { findMergedDoctor, mergeDoctors, type MergedDoctor } from "@/lib/doctor-merge";
import { PageShell } from "@/components/PageShell";
import {
  specialties,
  locations,
  surgeries,
  contact,
} from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import doctorMale1 from "@/assets/doctor-male-1.jpg";
import doctorMale2 from "@/assets/doctor-male-2.jpg";
import doctorFemale1 from "@/assets/doctor-female-1.jpg";
import doctorFemale2 from "@/assets/doctor-female-2.jpg";

const SITE = "https://healic.in";
const malePortraits = [doctorMale1, doctorMale2];
const femalePortraits = [doctorFemale1, doctorFemale2];

function hashOf(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function portraitFor(d: MergedDoctor): string {
  if (d.imageUrl) return d.imageUrl;
  const pool = d.gender === "female" ? femalePortraits : malePortraits;
  return pool[hashOf(d.name) % pool.length];
}

function ratingFor(d: MergedDoctor) {
  const years = parseInt(String(d.experience ?? "").match(/\d+/)?.[0] ?? "0", 10);
  if (years > 12) return 5.0;
  if (years >= 10) return 4.9;
  return 4.8;
}


function feeFor(d: MergedDoctor) {
  if (d.slug === "manish-daksh") return "Please confirm with hospital";
  const tiers = [249, 299, 399, 499, 599];
  return tiers[hashOf(d.name) % tiers.length];
}

function scheduleFor(d: MergedDoctor) {
  const blocks = [
    ["Mon–Wed / Fri–Sun", "8:00 AM – 5:00 PM"],
    ["Mon–Sat", "9:00 AM – 6:00 PM"],
    ["Tue–Sun", "10:00 AM – 7:00 PM"],
    ["Mon–Fri", "9:30 AM – 5:30 PM"],
  ];
  return blocks[hashOf(d.name + "s") % blocks.length];
}

const SPECIALTY_EXPERTISE: Record<string, string[]> = {
  "gynaecology-obstetrics": [
    "Normal & C-section deliveries",
    "High-risk pregnancy care",
    "Laparoscopic gynae surgery",
    "Hysterectomy (open & laparoscopic)",
    "Ovarian cyst & fibroid removal",
    "Infertility evaluation",
    "Menstrual disorder management",
    "Antenatal & postnatal care",
  ],
  "general-surgery": [
    "Laparoscopic hernia repair",
    "Gallbladder stone removal",
    "Laser piles & fissure surgery",
    "Fistula & pilonidal sinus",
    "Appendectomy",
    "Lipoma & cyst excision",
    "Wound care & debridement",
  ],
  "orthopaedics": [
    "Total knee replacement",
    "Hip replacement surgery",
    "Arthroscopy (knee & shoulder)",
    "Fracture fixation & plating",
    "Sports injury management",
    "Trigger finger release",
    "Carpal tunnel surgery",
  ],
  "ophthalmology": [
    "Phaco cataract surgery",
    "FLACS laser cataract",
    "Corneal transplant (keratoplasty)",
    "Vitrectomy & retinal care",
    "Glaucoma management",
    "Refractive error correction",
  ],
  "ent": [
    "Septoplasty for deviated septum",
    "Tonsillectomy & adenoidectomy",
    "Tympanoplasty (ear drum repair)",
    "Mastoidectomy",
    "Nasal bone correction",
    "Endoscopic sinus surgery",
  ],
  "urology": [
    "PCNL for kidney stones",
    "Ureteroscopy (URS) stone removal",
    "TURP for enlarged prostate",
    "TURBT bladder tumour resection",
    "Urethroplasty & stricture care",
    "Cystectomy",
  ],
  "general-physician": [
    "Fever & infection management",
    "Diabetes & hypertension care",
    "Respiratory infections",
    "Headache & migraine",
    "Digestive disorders",
    "Routine health check-ups",
    "Preventive health counselling",
  ],
  "paediatrics": [
    "Newborn & infant care",
    "Childhood immunisation",
    "Growth & development checks",
    "Paediatric infections",
    "Asthma & allergy care",
    "Nutritional counselling",
  ],
  "anaesthesia": [
    "General anaesthesia",
    "Spinal & epidural anaesthesia",
    "Caudal block",
    "Pain management",
    "Pre-anaesthetic evaluation",
    "ICU & critical care support",
  ],
};

function expertiseFor(d: MergedDoctor, relatedSurgeryNames: string[]): string[] {
  const fromAbout: string[] = [];
  if (d.about) {
    const m = d.about.match(/Areas of Expertise[:\s]*([\s\S]*?)(?:\n\s*\n|$)/i);
    if (m) {
      m[1]
        .split(/\n|•|·|\*|;|,/)
        .map((s) => s.replace(/^[-–\s]+/, "").trim())
        .filter((s) => s.length > 2 && s.length < 90)
        .forEach((s) => fromAbout.push(s));
    }
  }

  const specialtyList = SPECIALTY_EXPERTISE[d.specialtySlug ?? ""] ?? [];
  const generic = [
    `Consultation & diagnosis in ${d.specialty}`,
    "Planned surgery & post-op follow-up",
    "Second opinion & treatment planning",
  ];

  const combined: string[] = [];
  const push = (s: string) => {
    const t = s.trim();
    if (!t) return;
    if (combined.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    combined.push(t);
  };

  fromAbout.forEach(push);
  relatedSurgeryNames.forEach(push);
  specialtyList.forEach(push);
  generic.forEach(push);

  const min = 5;
  const max = 10;
  while (combined.length < min) combined.push(generic[combined.length % generic.length]);
  return combined.slice(0, max);
}

function buildDoctorFaqs(d: MergedDoctor) {
  const loc = locations.find((l) => l.slug === d.locationSlug);
  const where = loc ? `Healic — ${loc.city}` : "Healic Multispeciality Hospital";
  return [
    {
      q: `How do I book an appointment with ${d.name}?`,
      a: `Call ${contact.phone}, WhatsApp our care team or fill the booking form on this page — we'll confirm a slot at ${where}.`,
    },
    {
      q: `What is ${d.name}'s specialty?`,
      a: `${d.name} is a ${d.specialty}${d.experience ? ` with ${d.experience} of clinical experience` : ""}.`,
    },
    {
      q: `Is online consultation available?`,
      a: `Yes — online video consultations are available alongside in-hospital visits. Same-day slots are usually possible.`,
    },
    {
      q: `Does Healic accept insurance / cashless?`,
      a: `Healic is empanelled with most major insurers and TPAs. Our front-desk team helps with pre-authorisation paperwork.`,
    },
    {
      q: `Do you offer fixed-price packages for planned surgery?`,
      a: `Yes. Every planned surgery comes with a written, all-inclusive estimate before admission — no billing surprises.`,
    },
  ];
}

function locationFromUrlSlug(slug: string): string | undefined {
  const s = slug.toLowerCase();
  if (s.includes("indirapuram") || s.includes("ghaziabad")) return "indirapuram-ghaziabad";
  if (s.includes("gurgaon") || s.includes("gurugram") || s.includes("sohna")) return "sohna-road-gurugram";
  if (s.includes("haridwar")) return "haridwar";
  if (s.includes("agra")) return "agra";
  return undefined;
}

export const Route = createFileRoute("/doctors/$slug")({
  beforeLoad: ({ params }) => {
    // Dr. Akash Singh's Gurgaon profile has been replaced by Dr. Narendra Kishor Mandal.
    if (params.slug === "dr-akash-singh-general-surgery-gurgaon") {
      throw redirect({
        to: "/doctors/$slug",
        params: { slug: "dr-narendra-kishor-mandal-general-surgeon-gurgaon" },
        replace: true,
        statusCode: 301,
      });
    }
  },
  loader: async ({ params }) => {
    // Old healic.in long slugs resolve to the same doctor (no redirect), except
    // for specific overrides handled in beforeLoad above.
    const resolvedSlug = OLD_DOCTOR_SLUGS[params.slug] ?? params.slug;
    // Always try CMS first so real doctor photos (image_url) take precedence
    // over the static featuredDoctors entries, which don't have imageUrl.
    let doctor: MergedDoctor | undefined;
    try {
      const row = await getDoctorBySlug({ data: { slug: resolvedSlug } });
      if (row) doctor = findMergedDoctor(resolvedSlug, [row]);
    } catch {
      // ignore — fall back to static below
    }
    if (!doctor) doctor = findMergedDoctor(resolvedSlug);
    if (!doctor) throw notFound();
    // Override location from URL slug so the same doctor can appear at multiple centres.
    const urlLoc = locationFromUrlSlug(params.slug);
    if (urlLoc && locations.some((l) => l.slug === urlLoc)) {
      const match = locations.find((l) => l.slug === urlLoc);
      doctor = { ...doctor, locationSlug: urlLoc, hospital: match?.city ?? doctor.hospital };
    }
    return { doctor };
  },
  head: ({ params, loaderData }) => {
    const d = loaderData?.doctor;
    if (!d)
      return {
        meta: [
          { title: "Doctor not found — Healic Multispeciality Hospital" },
          { name: "robots", content: "noindex,follow" },
        ],
      };
    const loc = locations.find((l) => l.slug === d.locationSlug);
    const canonicalSlug = doctorProfileSlug(d.slug, d.name);
    const url = `${SITE}/doctors/${canonicalSlug}`;
    const image = `${SITE}${portraitFor(d)}`;
    const cityPart = loc ? ` in ${loc.city}` : "";
    const expPart = d.experience ? `${d.experience} experienced ` : "";
    const qualPart = d.qualifications ? ` (${d.qualifications})` : "";

    // Unique <60 char title where possible (CMS override wins)
    const baseTitle = `${d.name}${qualPart} — ${d.specialty}${cityPart}`;
    const autoTitle =
      baseTitle.length <= 60
        ? `${baseTitle} | Healic`
        : `${d.name} — ${d.specialty}${cityPart} | Healic`;
    const title = d.metaTitle?.trim() || autoTitle;

    // Unique <160 char description (CMS override wins)
    const autoDescription =
      `Consult ${d.name}, ${expPart}${d.specialty}${cityPart}` +
      `${d.hospital ? ` at ${d.hospital}` : ""}. Book in-hospital or online appointment — fixed-price planned surgery, transparent care.`;
    const description = d.metaDescription?.trim() || autoDescription;


    const rating = ratingFor(d);
    const yearsExp = parseInt(String(d.experience ?? "").match(/\d+/)?.[0] ?? "0", 10);
    const reviewCount = 50 + (hashOf(d.name) % 350);

    const physicianLd = {
      "@context": "https://schema.org",
      "@type": "Physician",
      name: d.name,
      medicalSpecialty: d.specialty,
      url,
      image,
      telephone: contact.phoneTel,
      ...(d.qualifications && { description: d.qualifications }),
      ...(yearsExp > 0 && {
        hasOccupation: {
          "@type": "Occupation",
          name: d.specialty,
          experienceRequirements: {
            "@type": "OccupationalExperienceRequirements",
            monthsOfExperience: yearsExp * 12,
          },
        },
      }),
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: rating.toFixed(1),
        bestRating: "5",
        worstRating: "1",
        ratingCount: reviewCount,
        reviewCount,
      },
      ...(loc && {
        address: {
          "@type": "PostalAddress",
          streetAddress: loc.address,
          addressLocality: loc.city,
          addressCountry: "IN",
        },
        worksFor: { "@type": "Hospital", name: `Healic — ${loc.city}`, address: loc.address },
      }),
    };


    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Doctors", item: `${SITE}/doctors` },
        { "@type": "ListItem", position: 3, name: d.name, item: url },
      ],
    };

    const faqs = buildDoctorFaqs(d);
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "author", content: d.name },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "profile" },
        { property: "og:site_name", content: "Healic Multispeciality Hospital" },
        { property: "og:image", content: image },
        { property: "og:image:alt", content: `${d.name}, ${d.specialty}${cityPart}` },
        { property: "profile:first_name", content: d.name.split(" ")[1] ?? d.name },
        { property: "profile:last_name", content: d.name.split(" ").slice(-1)[0] },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: `${d.name} — ${d.specialty}` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(physicianLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
        { type: "application/ld+json", children: JSON.stringify(faqLd) },
      ],
    };
  },
  component: DoctorProfilePage,
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Doctor not found</h1>
        <p className="mt-3 text-muted-foreground">The doctor you're looking for isn't listed.</p>
        <Link
          to="/doctors"
          className="mt-6 inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to all doctors
        </Link>
      </div>
    </PageShell>
  ),
});

function DoctorProfilePage() {
  const { doctor: d } = Route.useLoaderData();
  const sp = specialties.find((s) => s.slug === d.specialtySlug);
  const loc = locations.find((l) => l.slug === d.locationSlug);
  const relatedSurgeries = surgeries.filter((s) => s.specialtySlug === d.specialtySlug).slice(0, 6);

  // Peers: pull from CMS list + static, exclude self.
  const fetchDoctors = useServerFn(listPublishedDoctors);
  const { data: cmsRows = [] } = useQuery({
    queryKey: ["public", "doctors"],
    queryFn: () => fetchDoctors(),
  });
  const peers = useMemo(
    () =>
      mergeDoctors(cmsRows)
        .filter((x) => x.specialtySlug === d.specialtySlug && x.slug !== d.slug)
        .slice(0, 4),
    [cmsRows, d.specialtySlug, d.slug],
  );

  const faqs = buildDoctorFaqs(d);
  const rating = ratingFor(d);
  const autoFee = feeFor(d);
  const inHospitalFee = d.consultFeeInhospital ?? autoFee;
  const onlineFee = d.consultFeeOnline ?? autoFee;
  const expertiseList = expertiseFor(d, relatedSurgeries.map((s) => s.name));
  const [autoDays, autoHours] = scheduleFor(d);
  const days = d.scheduleDays || autoDays;
  const hours = d.scheduleHours || autoHours;

  return (
    <PageShell>
      {/* TOP: Doctor card + Available at */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pt-10 lg:px-8">
          <Link
            to="/doctors"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent hover:opacity-80"
          >
            ← All Doctors
          </Link>

          <div className="mt-5 grid gap-5 lg:grid-cols-[1.7fr_1fr]">
            {/* Doctor identity card */}
            <article className="rounded-3xl border border-border bg-background p-5 shadow-soft sm:p-6">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                <div className="bg-doctor-gradient h-32 w-32 shrink-0 overflow-hidden rounded-2xl sm:h-36 sm:w-36">
                  <img
                    src={portraitFor(d)}
                    alt={`${d.name} — ${d.specialty}`}
                    loading="lazy"
                    decoding="async"
                    width={288}
                    height={288}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl">
                    {d.name}
                  </h1>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-accent">
                    <span className="font-medium">{d.specialty}</span>
                    {d.experience && (
                      <>
                        <span className="text-muted-foreground">|</span>
                        <span className="font-semibold">{d.experience} Experience</span>
                      </>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{days}</span>
                    <span className="font-semibold text-foreground">{hours}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold">
                      <Star className="h-4 w-4 fill-accent text-accent" />
                      {rating}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-pill bg-accent-soft px-2.5 py-1 text-[11px] font-semibold text-white">
                      <BadgeCheck className="h-3.5 w-3.5 text-white" /> Verified Doctor
                    </span>
                  </div>
                  <a
                    href="#book"
                    className="mt-5 inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-cta hover:opacity-90"
                  >
                    <Calendar className="h-4 w-4" /> Book Appointment
                  </a>
                </div>
              </div>
            </article>

            {/* Available at */}
            <aside className="rounded-3xl border border-border bg-cream p-5 sm:p-6">
              <p className="font-display text-lg font-semibold">Available at:</p>
              {loc ? (
                <div className="mt-4 rounded-2xl border border-border bg-background p-5">
                  <p className="font-display text-base font-semibold">{loc.city}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {loc.address}
                  </p>
                  <span className="mt-3 inline-block rounded-pill bg-accent-soft px-2.5 py-1 text-[11px] font-semibold text-accent">
                    24hr
                  </span>
                  <div className="mt-3">
                    <Link
                      to={locationHref(loc.slug)}
                      className="inline-flex items-center gap-1 rounded-pill border border-border bg-background px-4 py-2 text-xs font-semibold hover:border-accent"
                    >
                      View Map <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm text-muted-foreground">
                  Consults across Healic centres. Call us for the nearest slot.
                </p>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* About + Consultation / Expertise */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-10">
            <div>
              <h2 className="font-display text-3xl font-bold">About {d.name}</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {(d.about
                  ? d.about
                      .replace(/\s*Areas of Expertise[\s\S]*?(?=\n\s*\n|$)/gi, "")
                      .replace(new RegExp(`^\\s*About\\s+${d.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[:\\-–]?\\s*`, "i"), "")
                      .trim()
                  : null) ??
                  `${d.name} is a ${d.specialty}${
                    d.experience ? ` with ${d.experience} of clinical experience` : ""
                  }, committed to delivering comprehensive, evidence-based care. Patients are seen for diagnosis, planned surgery, second opinions and structured follow-up${
                    loc ? ` at Healic — ${loc.city}` : " across Healic Hospitals"
                  }.`}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                The approach combines modern diagnostics with a calm, patient-first
                conversation — so you understand your condition, the treatment plan and
                expected recovery before anything begins.
              </p>
            </div>

            {(() => {
              const rawEdu =
                d.education && d.education.length > 0
                  ? d.education
                  : d.qualifications
                    ? d.qualifications.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean)
                    : [
                        `${d.specialty} — Postgraduate training`,
                        `Clinical fellowship in ${sp?.name ?? d.specialty}`,
                      ];
              const { education: eduItems, awards, memberships } = partitionEducation(rawEdu);
              return (
                <>
                  <div>
                    <h2 className="font-display text-2xl font-bold">
                      <span className="inline-flex items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-accent" /> Education &amp; Training
                      </span>
                    </h2>
                    <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                      {(eduItems.length > 0 ? eduItems : rawEdu).map((q: string) => (
                        <li key={q} className="flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {memberships.length > 0 && (
                    <div>
                      <h2 className="font-display text-2xl font-bold">
                        <span className="inline-flex items-center gap-2">
                          <Users className="h-5 w-5 text-accent" /> Memberships
                        </span>
                      </h2>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {memberships.map((q) => (
                          <li key={q} className="flex items-start gap-2">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {awards.length > 0 && (
                    <div>
                      <h2 className="font-display text-2xl font-bold">
                        <span className="inline-flex items-center gap-2">
                          <Award className="h-5 w-5 text-accent" /> Awards &amp; Honours
                        </span>
                      </h2>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {awards.map((q) => (
                          <li key={q} className="flex items-start gap-2">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}


            {/* Work Experience */}
            <div>
              <h2 className="font-display text-2xl font-bold">
                <span className="inline-flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-accent" /> Work Experience
                </span>
              </h2>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {(d.workExperience && d.workExperience.length > 0
                  ? d.workExperience
                  : [
                      `Consultant — Healic Hospitals${loc ? `, ${loc.city}` : ""}`,
                      d.experience
                        ? `${d.experience} treating ${sp?.name ?? d.specialty} patients`
                        : `Active practice in ${sp?.name ?? d.specialty}`,
                      "Specialist consultant for planned surgery & post-op follow-up",
                    ]
                ).map((q: string) => (
                  <li key={q} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>

            {/* Procedures */}
            {relatedSurgeries.length > 0 && (
              <div>
                <h2 className="font-display text-2xl font-bold">
                  <span className="inline-flex items-center gap-2">
                    <Stethoscope className="h-5 w-5 text-accent" /> Procedures Performed
                  </span>
                </h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {relatedSurgeries.map((s) => (
                    <Link
                      key={s.slug}
                      to="/surgeries/$slug"
                      params={{ slug: s.slug }}
                      className="group rounded-2xl border border-border bg-background p-4 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-card"
                    >
                      <p className="font-display text-sm font-semibold">{s.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{s.short}</p>
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-accent opacity-0 transition group-hover:opacity-100">
                        Learn more about {s.name} <ArrowRight className="h-3 w-3" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Consultation Types + Expertise */}
          <aside className="space-y-5 self-start">
            <div className="rounded-3xl border border-border bg-background shadow-soft">
              <div className="border-b border-border p-5">
                <p className="font-display text-base font-semibold">Consultation Types</p>
              </div>
              <ul className="divide-y divide-border text-sm">
                <li className="flex items-center justify-between gap-4 p-5">
                  <span className="font-medium">In-Hospital Consultation</span>
                  <span className="font-bold text-accent">{typeof inHospitalFee === "number" ? `₹${inHospitalFee}` : inHospitalFee}</span>
                </li>
                <li className="flex items-center justify-between gap-4 p-5">
                  <span className="font-medium">Online Consultation</span>
                  <span className="font-bold text-accent">{typeof inHospitalFee === "number" ? `₹${inHospitalFee}` : inHospitalFee}</span>
                </li>
              </ul>
            </div>


            <div className="rounded-3xl border border-border bg-cream p-5">
              <p className="font-display text-base font-semibold">Quick Info</p>
              <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent" /> {days} · {hours}
                </li>
                {loc && (
                  <li className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 text-accent" /> {loc.city}
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-accent" /> {contact.phone}
                </li>
              </ul>
            </div>

            <div className="rounded-3xl border border-border bg-background p-5 shadow-soft">
              <p className="font-display text-base font-semibold">
                <span className="inline-flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-accent" /> Areas of Expertise
                </span>
              </p>
              <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
                {expertiseList.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Book + FAQs */}
      <section id="book" className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <DoctorBookingForm doctorName={d.name} inHospitalFee={inHospitalFee} />


            <div className="space-y-5">
              <div>
                <h2 className="font-display text-2xl font-bold">Frequently Asked Questions</h2>
                <div className="mt-4 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background">
                  {faqs.map((f) => (
                    <details key={f.q} className="group p-5 open:bg-cream">
                      <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold">
                        {f.q}
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent transition group-open:rotate-45">
                          +
                        </span>
                      </summary>
                      <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-background p-6 shadow-soft">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-soft text-accent">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">
                  Still unsure about treatment?
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Our care team will guide you through diagnosis, treatment options and recovery.
                  Share your question and we'll respond quickly.
                </p>
                <a
                  href={`tel:${contact.phoneTel}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
                >
                  Get Expert Advice
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Peer doctors */}
      {peers.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-display text-2xl font-bold">
              Other {sp?.name ?? "specialists"}
            </h2>
            <Link
              to="/doctors"
              className="text-xs font-semibold text-accent hover:underline"
            >
              View all
            </Link>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {peers.map((p) => (
              <Link
                key={p.name}
                to="/doctors/$slug"
                params={{ slug: doctorProfileSlug(p.slug, p.name) }}
                className="group overflow-hidden rounded-2xl border border-border bg-background transition hover:-translate-y-1 hover:border-accent hover:shadow-card"
              >
                <div className="bg-doctor-gradient aspect-[4/3] overflow-hidden">
                  <img
                    src={portraitFor(p)}
                    alt={p.name}
                    loading="lazy"
                    width={288}
                    height={288}
                    className="h-full w-full object-cover transition group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <p className="font-display text-sm font-semibold">{p.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{p.specialty}</p>
                  {p.experience && (
                    <p className="mt-2 inline-block rounded-pill bg-accent-soft px-2.5 py-1 text-[11px] font-semibold text-accent">
                      {p.experience}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Contact & Support */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <h2 className="font-display text-2xl font-bold">Contact &amp; Support</h2>
          <p className="mt-1 text-sm text-muted-foreground">Quick help options get answers fast.</p>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            <ContactCard
              icon={<Phone className="h-5 w-5" />}
              title="7 Am to 12 Am Helpline"
              meta={contact.phone}
              note="Speak to our care team anytime"
              cta="Call Now"
              href={`tel:${contact.phoneTel}`}
            />
            <ContactCard
              icon={<Mail className="h-5 w-5" />}
              title="Email Support"
              meta={contact.email}
              note="Share reports or documents easily"
              cta="Email Now"
              href={`mailto:${contact.email}`}
              variant="secondary"
            />
            <ContactCard
              icon={<MessageCircle className="h-5 w-5" />}
              title="WhatsApp Chat"
              meta="Instant help. Quick replies."
              note="Share images & get confirmations fast"
              cta="Chat on WhatsApp"
              href={`https://wa.me/${contact.whatsapp}`}
              variant="whatsapp"
            />
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-accent">{label}</span>
      {children}
    </label>
  );
}

function ContactCard({
  icon,
  title,
  meta,
  note,
  cta,
  href,
  variant = "primary",
}: {
  icon: React.ReactNode;
  title: string;
  meta: string;
  note: string;
  cta: string;
  href: string;
  variant?: "primary" | "secondary" | "whatsapp";
}) {
  const btn =
    variant === "whatsapp"
      ? "bg-[#25D366] text-white"
      : variant === "secondary"
        ? "bg-accent text-accent-foreground"
        : "bg-primary text-primary-foreground";
  return (
    <div className="rounded-2xl border border-border bg-background p-6 text-center shadow-soft">
      <h3 className="font-display text-base font-semibold">{title}</h3>
      <div className="mt-4 flex items-center justify-center gap-2 text-sm">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-accent">
          {icon}
        </span>
        <div className="text-left">
          <p className="font-semibold">{meta}</p>
          <p className="text-xs text-muted-foreground">{note}</p>
        </div>
      </div>
      <a
        href={href}
        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-pill px-5 py-2.5 text-sm font-semibold ${btn}`}
      >
        {cta}
      </a>
    </div>
  );
}

function DoctorBookingForm({ doctorName, inHospitalFee }: { doctorName: string; inHospitalFee: number | string }) {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    if (name.length < 2) return toast.error("Please enter your name");
    if (!/^[0-9+\-\s()]{7,20}$/.test(phone)) return toast.error("Please enter a valid phone");
    setSubmitting(true);
    const ok = await notifyForm({
      formType: "Doctor Appointment Booking",
      source: `Doctor: ${doctorName}`,
      fields: [
        { label: "Doctor", value: doctorName },
        { label: "Name", value: name },
        { label: "Phone", value: phone },
        { label: "Preferred Date", value: String(fd.get("date") ?? "") },
        { label: "Preferred Time", value: String(fd.get("time") ?? "") },
        { label: "Consultation Type", value: String(fd.get("consult_type") ?? "") },
        { label: "Reason for visit", value: String(fd.get("reason") ?? "") },
      ],
    });
    setSubmitting(false);
    if (ok) {
      toast.success("Appointment requested! Our team will confirm shortly.");
      form.reset();
    } else {
      toast.error("Could not submit. Please call +91 95887 95887.");
    }
  };
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-3xl border border-border bg-background p-6 shadow-soft"
    >
      <h2 className="font-display text-2xl font-bold">Book an Appointment</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        With {doctorName} — quick, secure booking, no redirects.
      </p>
      <div className="mt-6 grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Preferred Date">
            <input name="date" type="date" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
          <Field label="Preferred Time">
            <input name="time" type="time" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
        </div>
        <Field label="Consultation Type">
          <select name="consult_type" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent">
            <option>In-Hospital Consultation ({typeof inHospitalFee === "number" ? `₹${inHospitalFee}` : inHospitalFee})</option>
            <option>Online Consultation ({typeof inHospitalFee === "number" ? `₹${inHospitalFee}` : inHospitalFee})</option>
          </select>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full Name">
            <input name="name" type="text" placeholder="Your full name" required className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
          <Field label="Mobile Number">
            <input name="phone" type="tel" placeholder="+91" required className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
        </div>
        <Field label="Reason for visit (optional)">
          <textarea name="reason" rows={3} placeholder="Share symptoms or what you'd like to discuss" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
        </Field>
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center justify-center gap-2 self-start rounded-xl bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-cta hover:opacity-90 disabled:opacity-60"
        >
          <Calendar className="h-4 w-4" /> {submitting ? "Submitting..." : "Confirm Appointment"}
        </button>
      </div>
    </form>
  );
}
